import { useEffect, useState } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { peopleGateway } from '../../index';
import { HelpTooltip } from '../../../../shared/components/HelpTooltip';
import { employeePayMethodLabels, type EmployeePayMethodResponse, type EmployeeResponse } from '../../types';
import { validateBankDetails, type BankDetailsErrors } from './bankValidation';

const DIRECT_DEPOSIT = 1;
const CHECK = 0;

/** Same wording the section shows when a Direct Deposit is entered but cannot be saved. Exported so the tests assert the exact text. */
export const DIRECT_DEPOSIT_UNAVAILABLE =
  'Direct deposit cannot be saved yet. Bank accounts need secure storage that is still under security review, so nothing was saved or sent.';

const EXTRA_WITHHOLDING_HELP =
  'Extra withholding is a tax election the employee makes in Taxes & Exemptions (Step 2). It is shown here for reference and is not entered or changed here.';

const usd = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** Pay Method and Bank Details (EPN-188, Figma admin screens 10 to 12). Direct Deposit reveals Bank Name, Routing Number and Account Number as
 * required fields with format checks; Check hides them and drops the requirement. Bank values live only in this component's state while the admin
 * types: they are never sent, stored, logged or shown back, because secure bank-account storage is not approved yet (SECURITY BLOCKER, see
 * docs/employee-sensitive-data-design.md). Choosing Direct Deposit therefore validates but cannot be saved until the server offers it in
 * `availableMethods`. The extra withholding amounts are the employee's tax election (ADR 0004), shown read-only from the same record Step 2 saves. */
export function EmployeePayMethodSection({ employee, onSaved }: { employee: EmployeeResponse; onSaved?: (() => void) | undefined }) {
  const inRouter = useInRouterContext();
  const [current, setCurrent] = useState<EmployeePayMethodResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [method, setMethod] = useState('');
  const [bankName, setBankName] = useState('');
  const [routing, setRouting] = useState('');
  const [account, setAccount] = useState('');
  const [bankErrors, setBankErrors] = useState<BankDetailsErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // The employee's own election, read-only here: 'loading' | 'unavailable' | the extra federal withholding per pay period (null = none recorded).
  const [extraFederal, setExtraFederal] = useState<'loading' | 'unavailable' | number | null>('loading');

  function apply(r: EmployeePayMethodResponse) {
    setCurrent(r);
    setMethod(r.current ? String(r.current.method) : '');
  }

  useEffect(() => {
    let cancelled = false;
    void peopleGateway
      .getEmployeePayMethod(employee.id)
      .then((r) => {
        if (!cancelled) apply(r);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the pay method.');
      });
    return () => {
      cancelled = true;
    };
  }, [employee.id]);

  useEffect(() => {
    let cancelled = false;
    void peopleGateway
      .getFederalTaxElection(employee.id)
      .then((r) => {
        if (!cancelled) setExtraFederal(r.current?.extraWithholding ?? null);
      })
      .catch(() => {
        if (!cancelled) setExtraFederal('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [employee.id]);

  const directDeposit = method === String(DIRECT_DEPOSIT);

  function choose(value: string) {
    setMethod(value);
    setSaved(false);
    setError(null);
    setBankErrors({});
  }

  async function save() {
    setError(null);
    setSaved(false);
    if (method === '') {
      setError('Please choose a pay method.');
      return;
    }
    if (directDeposit) {
      const errors = validateBankDetails({ bankName, routingNumber: routing, accountNumber: account });
      setBankErrors(errors);
      if (Object.keys(errors).length > 0) {
        setError('Complete the required bank details before saving.');
        return;
      }
      if (!current?.availableMethods.includes(DIRECT_DEPOSIT)) {
        // Valid, but there is nowhere approved to keep it: drop what was typed rather than hold it any longer than needed.
        setRouting('');
        setAccount('');
        setError(DIRECT_DEPOSIT_UNAVAILABLE);
        return;
      }
    }
    setSaving(true);
    try {
      apply(await peopleGateway.saveEmployeePayMethod(employee.id, { method: Number(method), effectiveDate: null }));
      setRouting('');
      setAccount('');
      setSaved(true);
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the pay method.');
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return (
      <section className="profile-section">
        <h3 className="profile-section-title">Pay Method and Bank Details</h3>
        <p className="error">{loadError}</p>
      </section>
    );
  }

  const step2 = `/people/${employee.id}/taxes-exemptions`;
  const federalText =
    extraFederal === 'loading' ? 'Loading…' : extraFederal === 'unavailable' ? 'Unavailable' : extraFederal === null ? 'None recorded' : `${usd(extraFederal)} per pay period`;

  return (
    <section className="profile-section">
      <h3 className="profile-section-title">Pay Method and Bank Details</h3>
      <div className="profile-field-grid" style={{ alignItems: 'end' }}>
        <label>
          Pay Method <span aria-hidden="true">*</span>
          <select value={method} onChange={(e) => choose(e.target.value)} disabled={!current} required>
            <option value="">Select a pay method</option>
            <option value={DIRECT_DEPOSIT}>{employeePayMethodLabels[DIRECT_DEPOSIT]}</option>
            <option value={CHECK}>{employeePayMethodLabels[CHECK]}</option>
          </select>
        </label>
        <div>
          <span id="extra-federal-label">
            Extra Federal Withholding <HelpTooltip label="About extra withholding" text={EXTRA_WITHHOLDING_HELP} />
          </span>
          <input aria-labelledby="extra-federal-label" readOnly disabled value={federalText} data-testid="extra-federal-withholding" />
        </div>
        <div>
          <span id="extra-state-label">Extra State Withholding</span>
          <input aria-labelledby="extra-state-label" readOnly disabled value="Not recorded yet" data-testid="extra-state-withholding" />
        </div>
      </div>
      <p className="muted" style={{ marginTop: 6 }}>
        {inRouter ? <>The employee sets withholding in <Link to={step2}>Taxes &amp; Exemptions (Step 2)</Link>; it is shown here for reference.</> : 'The employee sets withholding in Taxes & Exemptions (Step 2); it is shown here for reference.'}
      </p>

      {directDeposit && (
        <div className="profile-field-grid" style={{ marginTop: 12 }} data-testid="bank-fields">
          <label>
            Bank Name <span aria-hidden="true">*</span>
            <input
              value={bankName}
              maxLength={100}
              autoComplete="off"
              aria-required="true"
              aria-invalid={bankErrors.bankName ? true : undefined}
              onChange={(e) => { setBankName(e.target.value); setSaved(false); }}
            />
            {bankErrors.bankName && <span className="error" role="alert">{bankErrors.bankName}</span>}
          </label>
          <label>
            Bank Routing Number <span aria-hidden="true">*</span>
            <input
              inputMode="numeric"
              maxLength={11}
              autoComplete="off"
              aria-required="true"
              aria-invalid={bankErrors.routingNumber ? true : undefined}
              value={routing}
              onChange={(e) => { setRouting(e.target.value); setSaved(false); }}
            />
            {bankErrors.routingNumber && <span className="error" role="alert">{bankErrors.routingNumber}</span>}
          </label>
          <label>
            Bank Account Number <span aria-hidden="true">*</span>
            <input
              inputMode="numeric"
              maxLength={20}
              autoComplete="off"
              aria-required="true"
              aria-invalid={bankErrors.accountNumber ? true : undefined}
              value={account}
              onChange={(e) => { setAccount(e.target.value); setSaved(false); }}
            />
            {bankErrors.accountNumber && <span className="error" role="alert">{bankErrors.accountNumber}</span>}
          </label>
        </div>
      )}
      {directDeposit && !current?.availableMethods.includes(DIRECT_DEPOSIT) && (
        <p className="muted" role="note">
          Direct deposit can be filled in and checked here, but it cannot be saved until secure bank-account storage has been reviewed and approved.
          A direct deposit setting does not mean payments can be sent to this account.
        </p>
      )}
      {current?.current && (
        <p className="muted" style={{ marginTop: 10 }}>
          Current: {employeePayMethodLabels[current.current.method] ?? current.current.method}, effective {current.current.effectiveFrom}.
        </p>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      {saved && <p className="muted" role="status">Pay method saved.</p>}
      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={() => void save()} disabled={saving || !current}>
          {saving ? 'Saving…' : 'Save Pay Method'}
        </button>
      </div>
    </section>
  );
}
